import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { gql, useMutation } from "@apollo/client";
import { Button as LatitudeButton } from "@flexport/latitude-typescript";
import FileUpload from "../components/FileUpload";
import FileList, { FileListRefType } from "../components/FileList";
import { getUserData } from "../util/userAuth";
import { isSessionExpiredError } from "../contexts/SessionExpiredContext";

const MARK_UPLOADS_COMPLETE = gql`
  mutation MarkClientUploadsComplete(
    $clientId: ID!
    $comment: String!
    $markAsComplete: Boolean!
  ) {
    markClientUploadsComplete(
      clientId: $clientId
      comment: $comment
      markAsComplete: $markAsComplete
    ) {
      clientId
      client_name
      folder
      upload_complete
    }
  }
`;

// No additional client query needed; we derive base client data from currentUser

const PageContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  color: #333;
  margin: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Message = styled.div`
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid #dee2e6;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalTitle = styled.h2`
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 20px;
  color: #333;
`;

const ModalSubtitle = styled.p`
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 14px;
  color: #666;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  margin-bottom: 20px;

  &:focus {
    outline: none;
    border-color: #28a745;
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 10px;
`;

const CheckboxInput = styled.input.attrs({ type: "checkbox" })`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  cursor: pointer;
  user-select: none;
`;

interface ClientData {
  clientId: string;
  client_name: string;
  folder: string;
  upload_complete: boolean;
}

const FilesPage: React.FC = () => {
  const fileListRef = useRef<FileListRefType>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [clientData, setClientData] = useState<ClientData | null>(null);
  const [isNotifying, setIsNotifying] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [comment, setComment] = useState("");
  const [markAsComplete, setMarkAsComplete] = useState(false);

  const [markUploadsComplete] = useMutation(MARK_UPLOADS_COMPLETE);

  // Loading state is minimal; we show a brief message while userData is loading.
  const clientLoading = false;

  useEffect(() => {
    // Get user data and clientId from localStorage
    const fetchUserData = async () => {
      const data = await getUserData();
      setUserData(data);

      if (data && data.clientId) {
        setClientId(data.clientId);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    // Minimal presence of clientData via currentUser
    // We rely on currentUser() to provide base client info
    if (userData?.clientId && userData?.clientName) {
      setClientData(
        (prev) =>
          prev || {
            clientId: String(userData.clientId),
            client_name: userData.clientName,
            folder: userData.folderPath || "",
            upload_complete: false,
          },
      );
    }
  }, [userData]);

  const handleUploadComplete = (fileData?: any) => {
    if (fileListRef.current) {
      if (fileData) {
        // Add the new file immediately for optimistic UI update
        fileListRef.current.addNewFile(fileData);
      } else {
        // Fallback to refetch if no file data provided
        fileListRef.current.refetch();
      }
    }
  };

  const handleNotifyFlexportClick = () => {
    // Show the comment modal
    setShowCommentModal(true);
  };

  const handleModalCancel = () => {
    setShowCommentModal(false);
    setComment("");
    setMarkAsComplete(false);
  };

  const handleModalSubmit = async () => {
    if (!clientId) return;

    // Validate comment is not empty
    if (!comment.trim()) {
      alert("Please enter a comment before sending the notification.");
      return;
    }

    // Only proceed if not already notifying
    if (isNotifying) return;

    setIsNotifying(true);

    try {
      // Call the mutation with markAsComplete checkbox state
      const response = await markUploadsComplete({
        variables: {
          clientId: clientId,
          comment: comment.trim(),
          markAsComplete: markAsComplete,
        },
      });

      // Update local state with the response data
      if (response.data?.markClientUploadsComplete) {
        setClientData({
          ...response.data.markClientUploadsComplete,
        });
      }

      // Refetch the file list to get updated data
      if (fileListRef.current) {
        fileListRef.current.refetch();
      }

      // Close modal and reset state
      setShowCommentModal(false);
      setComment("");
      setMarkAsComplete(false);

      alert("Flexport Drawback brokers notified");
    } catch (error: unknown) {
      console.error("Error notifying Flexport:", error);
      // Skip alert when session-expired modal will handle the error
      if (!isSessionExpiredError()) {
        alert("Failed to notify Flexport. Please try again.");
      }
    } finally {
      setIsNotifying(false);
    }
  };

  if (!userData) {
    return (
      <PageContainer>
        <PageTitle>Flexport Drawback File Uploader</PageTitle>
        <Message>Loading user data...</Message>
      </PageContainer>
    );
  }

  if (userData.error) {
    return (
      <PageContainer>
        <PageTitle>Flexport Drawback File Uploader</PageTitle>
        <Message style={{ color: "red" }}>
          Authentication error: {userData.error.message}.
          <br />
          Please contact an administrator or try a different userId.
        </Message>
      </PageContainer>
    );
  }

  if (!clientId) {
    return (
      <PageContainer>
        <PageTitle>Flexport Drawback File Uploader</PageTitle>
        <Message>
          You don't have access to any client. Please contact an administrator.
        </Message>
      </PageContainer>
    );
  }

  if (clientLoading) {
    return (
      <PageContainer>
        <PageTitle>Flexport Drawback File Uploader</PageTitle>
        <Message>Loading client data...</Message>
      </PageContainer>
    );
  }

  // Only for displaying notification button
  const isCustomer = userData?.role === "CUSTOMER";

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Flexport Drawback File Uploader</PageTitle>
        {clientData && isCustomer && (
          <ButtonContainer>
            <LatitudeButton
              label="Notify Flexport"
              intent="basic"
              kind="hollow"
              size="m"
              isLoading={isNotifying}
              width="responsive"
              type="button"
              disabled={isNotifying}
              onClick={handleNotifyFlexportClick}
            >
              Notify Flexport
            </LatitudeButton>
          </ButtonContainer>
        )}
      </PageHeader>

      {/* Comment Modal */}
      {showCommentModal && (
        <ModalOverlay onClick={handleModalCancel}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Notify Flexport</ModalTitle>
            <ModalSubtitle>
              Enter any comments or notes for your drawback broker
            </ModalSubtitle>
            <ModalTextarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Type your message here"
              autoFocus
            />
            <CheckboxContainer>
              <CheckboxInput
                checked={markAsComplete}
                onChange={(e) => setMarkAsComplete(e.target.checked)}
                id="mark-complete-checkbox"
              />
              <CheckboxLabel htmlFor="mark-complete-checkbox">
                Mark file upload as completed
              </CheckboxLabel>
            </CheckboxContainer>
            <ModalButtonGroup>
              <LatitudeButton
                label="Cancel"
                intent="basic"
                kind="hollow"
                size="m"
                isLoading={false}
                width="responsive"
                type="button"
                disabled={isNotifying}
                onClick={handleModalCancel}
              >
                Cancel
              </LatitudeButton>
              <LatitudeButton
                label="Send"
                intent="basic"
                kind="solid"
                size="m"
                isLoading={isNotifying}
                width="responsive"
                type="button"
                disabled={isNotifying || !comment.trim()}
                onClick={handleModalSubmit}
              >
                {isNotifying ? "Sending..." : "Send"}
              </LatitudeButton>
            </ModalButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}

      {clientId && (
        <>
          <FileUpload
            clientId={clientId}
            userData={userData}
            onUploadComplete={handleUploadComplete}
            onUploadError={(error: Error) =>
              console.error("Upload error:", error)
            }
            allowedFileTypes={[
              ".jpg",
              ".jpeg",
              ".png",
              ".pdf",
              ".doc",
              ".docx",
              ".xls",
              ".xlsx",
              ".zip",
              ".csv",
            ]}
            maxParallelUploads={10}
          />

          <FileList ref={fileListRef} clientId={clientId} userData={userData} />
        </>
      )}
    </PageContainer>
  );
};

export default FilesPage;
